import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Input, Button, message, Tabs, Divider, ConfigProvider, App, Spin } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  NumberOutlined,
  QrcodeOutlined,
  WechatOutlined,
  MobileOutlined,
  SafetyOutlined,
  LoadingOutlined,
  ReloadOutlined,
  CloseCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { createStyles } from 'antd-style';
import { login, currentUser, getWechatQrcode, register, getWechatQrcode4Login, login4Scan, googleLogin } from '@/services/auth';
import { GOOGLE_CLIENT_ID } from '@/constants/config';
import IconGoogle from '@/assets/icons/IconGoogle.svg';
import { formatLocaleMessage as t, getAntdLocale } from '@/utils/i18n';

const useStyles = createStyles(({ token, css }) => ({
  modal: css`
    .ant-modal-content {
      padding: 0;
      overflow: hidden;
    }
    .ant-modal-close {
      top: 16px;
      right: 16px;
    }
  `,
  container: css`
    display: flex;
    min-height: 420px;
  `,
  leftPanel: css`
    width: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 32px;
    border-right: 1px solid ${token.colorBorderSecondary};
    background: ${token.colorBgLayout};
  `,
  rightPanel: css`
    flex: 1;
    padding: 32px 40px;
    display: flex;
    flex-direction: column;
  `,
  singlePanel: css`
    flex: 1;
    padding: 32px 48px;
    display: flex;
    flex-direction: column;
  `,
  panelTitle: css`
    font-size: 18px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 8px;
    color: ${token.colorText};
  `,
  panelSubtitle: css`
    font-size: 13px;
    color: ${token.colorTextSecondary};
    text-align: center;
    margin-bottom: 24px;
  `,
  qrcode: css`
    width: 160px;
    height: 160px;
    border: 1px solid ${token.colorBorder};
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    background: #fff;
  `,
  qrcodeHint: css`
    color: ${token.colorTextSecondary};
    font-size: 13px;
    text-align: center;
    line-height: 1.6;
  `,
  formContainer: css`
    flex: 1;
    display: flex;
    flex-direction: column;
  `,
  formInput: css`
    height: 44px;
    border-radius: 8px;
    .ant-input-prefix {
      margin-right: 12px;
      color: ${token.colorTextSecondary};
    }
  `,
  captchaRow: css`
    display: flex;
    gap: 12px;
  `,
  captchaInput: css`
    flex: 1;
  `,
  captchaBox: css`
    width: 116px;
    height: 44px;
    padding: 0;
    border: 1px solid ${token.colorBorder};
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
    background: ${token.colorBgContainer};
    overflow: hidden;
    box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.03);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: ${token.colorPrimary};
      box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.03), 0 0 0 2px color-mix(in srgb, ${token.colorPrimaryBg} 48%, transparent);
    }
  `,
  captchaCanvas: css`
    width: 100%;
    height: 100%;
    display: block;
  `,
  submitBtn: css`
    height: 44px;
    border-radius: 8px;
    font-size: 15px;
  `,
  tabs: css`
    .ant-tabs-nav {
      margin-bottom: 24px;
    }
    .ant-tabs-nav::before {
      border-bottom: none;
    }
    .ant-tabs-tab {
      padding: 8px 0;
      font-size: 16px;
      font-weight: 500;
    }
    .ant-tabs-ink-bar {
      height: 3px;
      border-radius: 2px;
    }
  `,
  thirdPartySection: css`
    margin-top: auto;
    padding-top: 16px;
  `,
  thirdPartyDivider: css`
    margin: 16px 0;
    .ant-divider-inner-text {
      color: ${token.colorTextSecondary};
      font-size: 13px;
    }
  `,
  thirdPartyBtns: css`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
  `,
  thirdPartyBtn: css`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid ${token.colorBorder};
    background: ${token.colorBgContainer};
    &:hover {
      border-color: ${token.colorPrimary};
      color: ${token.colorPrimary};
    }
  `,
  thirdPartyBtnLoading: css`
    position: relative;
    pointer-events: none;
    opacity: 0.72;
  `,
  thirdPartyBtnSpinning: css`
    animation: googleLoginSpin 0.9s linear infinite;

    @keyframes googleLoginSpin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  `,
  thirdPartyHint: css`
    margin-top: 12px;
    font-size: 12px;
    color: ${token.colorTextSecondary};
    text-align: center;
    min-height: 18px;
  `,
  googleBtn: css`
    &:hover {
      border-color: #4285F4;
      background: #e8f0fe;
    }
  `,
  wechatBtn: css`
    color: #07c160;
    &:hover {
      border-color: #07c160;
      background: #f6ffed;
    }
  `,
  bindingSection: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
  `,
  backLink: css`
    color: ${token.colorTextSecondary};
    cursor: pointer;
    font-size: 13px;
    margin-top: 16px;
    &:hover {
      color: ${token.colorPrimary};
    }
  `,
  qrcodeWrapper: css`
    position: relative;
    width: 160px;
    height: 160px;
  `,
  qrcodeOverlay: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    z-index: 1;
  `,
  overlayError: css`
    color: ${token.colorError};
  `,
  overlayText: css`
    font-size: 13px;
    color: ${token.colorTextSecondary};
    margin-top: 8px;
    text-align: center;
  `,
  refreshBtn: css`
    margin-top: 12px;
  `,
}));

type MathCaptchaState = {
  expression: string;
  result: number;
  seed: string;
};

const createSeededRandom = (seed: string) => {
  let value = 0;
  for (let i = 0; i < seed.length; i += 1) {
    value = (value * 31 + seed.charCodeAt(i)) >>> 0;
  }

  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0xffffffff;
  };
};

// 生成数学验证码
const generateCaptcha = (): MathCaptchaState => {
  const ops = ['+', '-'];
  const a = Math.floor(Math.random() * 10);
  const b = Math.floor(Math.random() * 10);
  const c = Math.floor(Math.random() * 10);
  const op1 = ops[Math.floor(Math.random() * 2)];
  const op2 = ops[Math.floor(Math.random() * 2)];
  const expression = `${a}${op1}${b}${op2}${c}`;
  // eslint-disable-next-line no-eval
  const result = eval(expression);
  return { expression, result, seed: `${expression}-${Math.random().toString(36).slice(2, 10)}` };
};

interface GraphicCaptchaProps {
  captcha: MathCaptchaState;
  onRefresh: () => void;
  className: string;
  canvasClassName: string;
  title: string;
}

const GraphicCaptcha: React.FC<GraphicCaptchaProps> = ({ captcha, onRefresh, className, canvasClassName, title }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = 116;
    const height = 44;
    const ratio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    canvas.width = width * ratio;
    canvas.height = height * ratio;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.scale(ratio, ratio);
    context.clearRect(0, 0, width, height);

    const random = createSeededRandom(captcha.seed);
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#fcfdff');
    gradient.addColorStop(1, '#f4f8ff');
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    for (let i = 0; i < 3; i += 1) {
      context.strokeStyle = `rgba(64, 110, 255, ${0.12 + random() * 0.12})`;
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(random() * 18, random() * height);
      context.bezierCurveTo(
        width * (0.25 + random() * 0.15),
        random() * height,
        width * (0.55 + random() * 0.15),
        random() * height,
        width - random() * 18,
        random() * height,
      );
      context.stroke();
    }

    for (let i = 0; i < 18; i += 1) {
      context.fillStyle = `rgba(34, 83, 214, ${0.1 + random() * 0.12})`;
      context.beginPath();
      context.arc(random() * width, random() * height, 0.8 + random() * 1.4, 0, Math.PI * 2);
      context.fill();
    }

    const chars = captcha.expression.split('');
    const startX = 18;
    const gap = 20;
    chars.forEach((char, index) => {
      const fontSize = 18 + Math.round(random() * 2);
      const rotate = (random() - 0.5) * 0.28;
      const x = startX + index * gap + (random() - 0.5) * 3;
      const y = 27 + (random() - 0.5) * 5;
      const palette = ['#1f1f1f', '#2b2f36', '#2f54eb', '#434343'];
      const color = palette[Math.floor(random() * palette.length)];

      context.save();
      context.translate(x, y);
      context.rotate(rotate);
      context.fillStyle = color;
      context.font = `700 ${fontSize}px AlibabaSans, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(char, 0, 0);
      context.restore();
    });
  }, [captcha]);

  return (
    <button type="button" className={className} onClick={onRefresh} title={title} aria-label={title}>
      <canvas ref={canvasRef} className={canvasClassName} />
    </button>
  );
};

type ViewMode = 'default' | 'wechat' | 'binding';

interface LoginModalContentProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  affCode?: string;
  defaultTab?: 'login' | 'register';
}

// 纯净的 Modal 内容组件，不依赖任何 UmiJS 上下文
const LoginModalContent: React.FC<LoginModalContentProps> = ({ open, onClose, onSuccess, affCode, defaultTab }) => {
  const { styles, cx } = useStyles();
  const { message: messageApi } = App.useApp();
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [googleLoginLoading, setGoogleLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'register'>(defaultTab || 'login');
  const [viewMode, setViewMode] = useState<ViewMode>('default');
  const [captcha, setCaptcha] = useState(() => generateCaptcha());
  const [registerCaptcha, setRegisterCaptcha] = useState(() => generateCaptcha());

  // 注册流程相关状态
  const [qrcodeUrl, setQrcodeUrl] = useState<string>('');
  const [ticket, setTicket] = useState<string>('');
  const [qrcodeLoading, setQrcodeLoading] = useState(false);
  const [scanProcessing, setScanProcessing] = useState(false); // 扫码后请求处理中
  const [scanError, setScanError] = useState(false); // 扫码登录失败状态
  const [savedClientAccount, setSavedClientAccount] = useState<string>(''); // 保存的项目编号（用于注册失败回填）
  const eventSourceRef = useRef<EventSource | null>(null);
  const googleTokenClientRef = useRef<any>(null);
  const googleLoginLoadingRef = useRef(false);

  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    loginForm.setFieldValue('captcha', '');
  }, [loginForm]);

  const refreshRegisterCaptcha = useCallback(() => {
    setRegisterCaptcha(generateCaptcha());
    registerForm.setFieldValue('captcha', '');
  }, [registerForm]);

  useEffect(() => {
    if (open) {
      loginForm.resetFields();
      registerForm.resetFields();
      setActiveTab(defaultTab || 'login');
      setViewMode('default');
      refreshCaptcha();
      refreshRegisterCaptcha();
      setQrcodeUrl('');
      setTicket('');
      setScanProcessing(false);
      setScanError(false);
      setSavedClientAccount('');
      setGoogleLoginLoading(false);
      // 如果有邀请码，设置到注册表单
      if (affCode) {
        registerForm.setFieldsValue({ affCode });
      }

      // 初始化 Google OAuth 弹窗登录
      setTimeout(() => {
        // @ts-ignore
        if (window.google?.accounts?.oauth2) {
          // @ts-ignore
          googleTokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'openid email profile',
            callback: onGoogleLoginSuccess,
            error_callback: (error: any) => {
              googleLoginLoadingRef.current = false;
              setGoogleLoginLoading(false);
              const errorMsg = error?.message || error?.type || t('components.loginModal.loginFailed', 'Login failed, please try again!');
              messageApi.error(errorMsg);
            },
          });
        }
      }, 300);
    } else {
      // 关闭弹窗时清理 SSE 连接
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    }
  }, [open, loginForm, registerForm, refreshCaptcha, refreshRegisterCaptcha]);

  // 组件卸载时清理 SSE 连接
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const handleLogin = async (values: any) => {
    if (Number(values.captcha) !== captcha.result) {
      messageApi.error(t('components.loginModal.captchaError', 'Incorrect captcha result'));
      refreshCaptcha();
      return;
    }

    setLoading(true);
    try {
      const { data: token } = await login({
        clientAccount: values.clientAccount,
        username: values.username,
        password: values.password,
      });

      if (token) {
        localStorage.setItem('token', token);
      }

      messageApi.success(t('components.loginModal.loginSuccess', 'Login successful!'));
      onSuccess?.();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || t('components.loginModal.loginFailed', 'Login failed, please try again!');
      messageApi.error(errorMsg);
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const onGoogleLoginSuccess = async (response: any) => {
    try {
      const accessToken = response?.access_token;
      if (!accessToken) {
        throw new Error(t('components.loginModal.googleAccessTokenMissing', 'Google access token was not obtained'));
      }

      const { data: token } = await googleLogin({
        accessToken,
        affCode,
      });

      if (token) {
        localStorage.setItem('token', token);
        messageApi.success(t('components.loginModal.loginSuccess', 'Login successful!'));
        onSuccess?.();
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || t('components.loginModal.loginFailed', 'Login failed, please try again!');
      messageApi.error(errorMsg);
    } finally {
      googleLoginLoadingRef.current = false;
      setGoogleLoginLoading(false);
    }
  };

  const handleGoogleBtnClick = () => {
    if (googleLoginLoadingRef.current) {
      return;
    }

    if (googleTokenClientRef.current) {
      googleLoginLoadingRef.current = true;
      setGoogleLoginLoading(true);
      // @ts-ignore
      googleTokenClientRef.current.requestAccessToken({ prompt: 'select_account' });
    } else {
      messageApi.warning(t('components.loginModal.googleBlocked', 'Google login is still loading or blocked. Please check your network.'));
    }
  };

  const handleRegister = async (values: any) => {
    if (Number(values.captcha) !== registerCaptcha.result) {
      messageApi.error(t('components.loginModal.captchaError', 'Incorrect captcha result'));
      refreshRegisterCaptcha();
      return;
    }

    // 保存项目编号，用于注册失败时回填
    setSavedClientAccount(values.clientAccount);

    setLoading(true);
    setQrcodeLoading(true);

    try {
      // 调用后端获取微信二维码
      const { data } = await getWechatQrcode({
        partnerKey: values.clientAccount,
        account: values.username,
        affCode: values.affCode,
      });

      if (!data || !data.qrcode || !data.ticket) {
        messageApi.error(t('components.loginModal.fetchQrcodeFailed', 'Failed to get QR code, please try again'));
        return;
      }

      setQrcodeUrl(`data:image/png;base64,${data.qrcode}`);
      setTicket(data.ticket);
      setViewMode('binding');

      // 建立 SSE 连接等待扫码结果
      const sseUrl = `/edu3Api/sse/api/connect4Scan?ticket=${data.ticket}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      // 监听扫码成功事件
      eventSource.addEventListener('scan-qrcode-ai-nexus', async (event) => {
        try {
          const result = JSON.parse(event.data);
          const scanData = result.value || result;
          if (scanData.openid && scanData.unionid) {
            // 扫码成功，关闭 SSE 连接
            eventSource.close();
            eventSourceRef.current = null;

            // 显示处理中状态
            setScanProcessing(true);

            try {
              // 调用注册接口
              await register({
                ticket: data.ticket,
                openid: scanData.openid,
                unionid: scanData.unionid,
              });

              messageApi.success(t('components.loginModal.registerSuccess', 'Registration successful!'));
              onSuccess?.();
            } catch (registerError: any) {
              // 注册失败，回到上一步并填充项目编号
              const errorMsg = registerError?.response?.data?.message || registerError?.message || t('components.loginModal.registerFailed', 'Registration failed, please try again');
              messageApi.error(errorMsg);

              // 回到默认视图
              setViewMode('default');
              setActiveTab('register');
              // 填充项目编号
              registerForm.setFieldsValue({ clientAccount: values.clientAccount });
              refreshRegisterCaptcha();
            } finally {
              setScanProcessing(false);
            }
          }
        } catch (e) {
          console.error('SSE 消息解析失败:', e);
        }
      });

      eventSource.onerror = (error) => {
        console.error('SSE 连接错误:', error);
        eventSource.close();
        eventSourceRef.current = null;
        messageApi.error(t('components.loginModal.scanDisconnected', 'QR scan connection was interrupted, please try again'));
        // 回到默认视图并填充项目编号
        setViewMode('default');
        setActiveTab('register');
        registerForm.setFieldsValue({ clientAccount: values.clientAccount });
        refreshRegisterCaptcha();
      };

    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || t('components.loginModal.registerFailed', 'Registration failed, please try again');
      messageApi.error(errorMsg);
      refreshRegisterCaptcha();
    } finally {
      setLoading(false);
      setQrcodeLoading(false);
    }
  };

  const handleWechatLogin = async () => {
    setViewMode('wechat');
    setQrcodeLoading(true);
    setQrcodeUrl('');
    setScanError(false);

    try {
      // 调用后端获取微信登录二维码
      const { data } = await getWechatQrcode4Login();

      if (!data || !data.qrcode || !data.ticket) {
        messageApi.error(t('components.loginModal.fetchQrcodeFailed', 'Failed to get QR code, please try again'));
        setViewMode('default');
        return;
      }

      setQrcodeUrl(`data:image/png;base64,${data.qrcode}`);
      setTicket(data.ticket);

      // 建立 SSE 连接等待扫码结果
      const sseUrl = `/edu3Api/sse/api/connect4Scan?ticket=${data.ticket}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      // 监听扫码成功事件
      eventSource.addEventListener('scan-qrcode-ai-nexus', async (event) => {
        try {
          const result = JSON.parse(event.data);
          const scanData = result.value || result;
          if (scanData.openid && scanData.unionid) {
            // 扫码成功，关闭 SSE 连接
            eventSource.close();
            eventSourceRef.current = null;

            // 显示处理中状态
            setScanProcessing(true);

            try {
              // 调用扫码登录接口
              const { data: token } = await login4Scan({
                openid: scanData.openid,
                unionid: scanData.unionid,
              });

              if (token) {
                localStorage.setItem('token', token);
              }

              messageApi.success(t('components.loginModal.loginSuccess', 'Login successful!'));
              onSuccess?.();
            } catch (loginError: any) {
              // 登录失败，显示失败蒙层
              const errorMsg = loginError?.response?.data?.message || loginError?.message || t('components.loginModal.loginFailed', 'Login failed, please try again!');
              messageApi.error(errorMsg);
              setScanError(true);
            } finally {
              setScanProcessing(false);
            }
          }
        } catch (e) {
          console.error('SSE 消息解析失败:', e);
        }
      });

      eventSource.onerror = (error) => {
        console.error('SSE 连接错误:', error);
        eventSource.close();
        eventSourceRef.current = null;
        messageApi.error(t('components.loginModal.scanDisconnected', 'QR scan connection was interrupted, please try again'));
        setScanError(true);
      };

    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || t('components.loginModal.fetchQrcodeFailed', 'Failed to get QR code, please try again');
      messageApi.error(errorMsg);
      setViewMode('default');
    } finally {
      setQrcodeLoading(false);
    }
  };

  // 刷新微信登录二维码
  const handleRefreshWechatQrcode = async () => {
    setScanError(false);
    setQrcodeLoading(true);

    // 关闭旧的 SSE 连接
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      const { data } = await getWechatQrcode4Login();

      if (!data || !data.qrcode || !data.ticket) {
        messageApi.error(t('components.loginModal.fetchQrcodeFailed', 'Failed to get QR code, please try again'));
        setScanError(true);
        return;
      }

      setQrcodeUrl(`data:image/png;base64,${data.qrcode}`);
      setTicket(data.ticket);

      // 重新建立 SSE 连接
      const sseUrl = `/edu3Api/sse/api/connect4Scan?ticket=${data.ticket}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.addEventListener('scan-qrcode-ai-nexus', async (event) => {
        try {
          const result = JSON.parse(event.data);
          const scanData = result.value || result;
          if (scanData.openid && scanData.unionid) {
            eventSource.close();
            eventSourceRef.current = null;

            setScanProcessing(true);

            try {
              const { data: token } = await login4Scan({
                openid: scanData.openid,
                unionid: scanData.unionid,
              });

              if (token) {
                localStorage.setItem('token', token);
              }

              messageApi.success(t('components.loginModal.loginSuccess', 'Login successful!'));
              onSuccess?.();
            } catch (loginError: any) {
              const errorMsg = loginError?.response?.data?.message || loginError?.message || t('components.loginModal.loginFailed', 'Login failed, please try again!');
              messageApi.error(errorMsg);
              setScanError(true);
            } finally {
              setScanProcessing(false);
            }
          }
        } catch (e) {
          console.error('SSE 消息解析失败:', e);
        }
      });

      eventSource.onerror = (error) => {
        console.error('SSE 连接错误:', error);
        eventSource.close();
        eventSourceRef.current = null;
        messageApi.error(t('components.loginModal.scanDisconnected', 'QR scan connection was interrupted, please try again'));
        setScanError(true);
      };

    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || t('components.loginModal.refreshQrcodeFailed', 'Failed to refresh QR code');
      messageApi.error(errorMsg);
      setScanError(true);
    } finally {
      setQrcodeLoading(false);
    }
  };

  const handleBackToDefault = () => {
    // 关闭 SSE 连接
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setViewMode('default');
    setQrcodeUrl('');
    setTicket('');
  };

  const renderLeftPanel = () => {
    if (viewMode !== 'wechat') return null;
    return (
      <div className={styles.leftPanel}>
        <div className={styles.panelTitle}>{t('components.loginModal.wechatScanLogin', 'WeChat Scan Login')}</div>
        <div className={styles.qrcodeWrapper}>
          <div className={styles.qrcode}>
            {qrcodeLoading ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
            ) : qrcodeUrl ? (
              <img src={qrcodeUrl} alt={t('components.loginModal.wechatQrcodeAlt', 'WeChat QR code')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <QrcodeOutlined style={{ fontSize: 100, color: '#d9d9d9' }} />
            )}
          </div>
          {/* 处理中蒙层 */}
          {scanProcessing && (
            <div className={styles.qrcodeOverlay}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
              <div className={styles.overlayText}>{t('components.loginModal.loggingIn', 'Signing in...')}</div>
            </div>
          )}
          {/* 失败蒙层 */}
          {scanError && !scanProcessing && (
            <div className={styles.qrcodeOverlay}>
              <Button
                type="primary"
                shape="circle"
                icon={qrcodeLoading ? <LoadingOutlined spin /> : <ReloadOutlined />}
                size="large"
                onClick={handleRefreshWechatQrcode}
                disabled={qrcodeLoading}
                title={t('components.loginModal.refreshQrcode', 'Refresh QR code')}
              />
              <div className={styles.overlayText}>{t('components.loginModal.clickRefresh', 'Click to refresh')}</div>
            </div>
          )}
        </div>
        <div className={styles.qrcodeHint}>{t('components.loginModal.wechatScanHint', 'Open WeChat and scan to authorize login')}</div>
      </div>
    );
  };

  const renderLoginForm = () => (
    <Form form={loginForm} onFinish={handleLogin} layout="vertical" requiredMark={false}>
      <Form.Item name="clientAccount" rules={[{ required: true, message: t('components.loginModal.loginForm.clientAccountRequired', 'Please enter the enterprise account') }]}> 
        <Input prefix={<NumberOutlined />} placeholder={t('components.loginModal.loginForm.clientAccountPlaceholder', 'Enterprise account')} className={styles.formInput} />
      </Form.Item>
      <Form.Item name="username" rules={[{ required: true, message: t('components.loginModal.loginForm.usernameRequired', 'Please enter the username') }]}> 
        <Input prefix={<UserOutlined />} placeholder={t('components.loginModal.loginForm.usernamePlaceholder', 'Username')} className={styles.formInput} />
      </Form.Item>
      <Form.Item name="password" rules={[{ required: true, message: t('components.loginModal.loginForm.passwordRequired', 'Please enter the password') }]}> 
        <Input.Password prefix={<LockOutlined />} placeholder={t('components.loginModal.loginForm.passwordPlaceholder', 'Password')} className={styles.formInput} />
      </Form.Item>
      <Form.Item name="captcha" rules={[{ required: true, message: t('components.loginModal.loginForm.captchaRequired', 'Please enter the captcha result') }]}> 
        <div className={styles.captchaRow}>
          <Input prefix={<SafetyOutlined />} placeholder={t('components.loginModal.loginForm.captchaPlaceholder', 'Captcha result')} className={cx(styles.formInput, styles.captchaInput)} />
          <GraphicCaptcha
            captcha={captcha}
            onRefresh={refreshCaptcha}
            className={styles.captchaBox}
            canvasClassName={styles.captchaCanvas}
            title={t('components.loginModal.clickRefreshShort', 'Refresh')}
          />
        </div>
      </Form.Item>
      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" block loading={loading} className={styles.submitBtn}>{t('components.loginModal.login', 'Login')}</Button>
      </Form.Item>
    </Form>
  );

  const renderRegisterForm = () => (
    <Form form={registerForm} onFinish={handleRegister} layout="vertical" requiredMark={false} initialValues={{ username: 'user', affCode }}>
      <Form.Item name="clientAccount" rules={[{ required: true, message: t('components.loginModal.registerForm.clientAccountRequired', 'Please enter the personal account ID') }]}> 
        <Input prefix={<NumberOutlined />} placeholder={t('components.loginModal.registerForm.clientAccountPlaceholder', 'Personal account ID')} className={styles.formInput} />
      </Form.Item>
      <Form.Item name="username" style={{ display: 'none' }}>
        <Input prefix={<UserOutlined />} readOnly className={styles.formInput} style={{ backgroundColor: '#f5f5f5' }} />
      </Form.Item>
      {affCode && (
        <Form.Item name="affCode">
          <Input prefix={<LinkOutlined />} readOnly className={styles.formInput} style={{ backgroundColor: '#f5f5f5' }} />
        </Form.Item>
      )}
      <Form.Item name="captcha" rules={[{ required: true, message: t('components.loginModal.loginForm.captchaRequired', 'Please enter the captcha result') }]}> 
        <div className={styles.captchaRow}>
          <Input prefix={<SafetyOutlined />} placeholder={t('components.loginModal.loginForm.captchaPlaceholder', 'Captcha result')} className={cx(styles.formInput, styles.captchaInput)} />
          <GraphicCaptcha
            captcha={registerCaptcha}
            onRefresh={refreshRegisterCaptcha}
            className={styles.captchaBox}
            canvasClassName={styles.captchaCanvas}
            title={t('components.loginModal.clickRefreshShort', 'Refresh')}
          />
        </div>
      </Form.Item>
      <Form.Item style={{ marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" block loading={loading} className={styles.submitBtn}>{t('components.loginModal.register', 'Register')}</Button>
      </Form.Item>
    </Form>
  );

  const renderThirdPartyLogin = () => (
    <div className={styles.thirdPartySection}>
      <Divider className={styles.thirdPartyDivider}>{t('components.loginModal.thirdPartyLogin', 'Other login methods')}</Divider>
      <div className={styles.thirdPartyBtns}>
        <div
          id="googleBtn"
          className={cx(
            styles.thirdPartyBtn,
            styles.googleBtn,
            googleLoginLoading && styles.thirdPartyBtnLoading,
          )}
          title={googleLoginLoading ? t('components.loginModal.googleProcessing', 'Google login in progress') : t('components.loginModal.googleLogin', 'Google Login')}
          onClick={handleGoogleBtnClick}
          aria-disabled={googleLoginLoading}
        >
          {googleLoginLoading ? (
            <LoadingOutlined className={styles.thirdPartyBtnSpinning} style={{ fontSize: 18, color: '#4285F4' }} />
          ) : (
            <img src={IconGoogle} alt="Google" width={24} height={24} />
          )}
        </div>
        <div className={cx(styles.thirdPartyBtn, styles.wechatBtn)} onClick={handleWechatLogin} title={t('components.loginModal.wechatLogin', 'WeChat Login')}>
          <WechatOutlined style={{ fontSize: 24 }} />
        </div>
      </div>
      <div className={styles.thirdPartyHint}>{googleLoginLoading ? t('components.loginModal.googleAuthProcessing', 'Processing Google authorization, please wait...') : ''}</div>
    </div>
  );

  const renderBindingView = () => (
    <div className={styles.singlePanel}>
      <div className={styles.bindingSection}>
        <div className={styles.panelTitle}>{t('components.loginModal.wechatRegisterTitle', 'Register via QR code')}</div>
        <div className={styles.panelSubtitle}>{t('components.loginModal.wechatRegisterSubtitle', 'Use WeChat to scan the QR code and complete registration')}</div>
        <div className={styles.qrcodeWrapper}>
          <div className={styles.qrcode}>
            {qrcodeLoading ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
            ) : qrcodeUrl ? (
              <img src={qrcodeUrl} alt={t('components.loginModal.wechatQrcodeAlt', 'WeChat QR code')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <QrcodeOutlined style={{ fontSize: 100, color: '#d9d9d9' }} />
            )}
          </div>
          {/* 处理中蒙层 */}
          {scanProcessing && (
            <div className={styles.qrcodeOverlay}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
              <div className={styles.overlayText}>{t('components.loginModal.registering', 'Registering...')}</div>
            </div>
          )}
        </div>
        <div className={styles.qrcodeHint} style={{ whiteSpace: 'pre-line' }}>{t('components.loginModal.wechatRegisterHint', 'Open WeChat and scan\nRegistration completes automatically after scanning')}</div>
        <div className={styles.backLink} onClick={handleBackToDefault}>{t('components.loginModal.back', 'Back')}</div>
      </div>
    </div>
  );

  const renderWechatLoginHint = () => (
    <div className={styles.rightPanel}>
      <div className={styles.bindingSection}>
        <div className={styles.panelTitle}>{t('components.loginModal.wechatLoginHintTitle', 'Scan to Login')}</div>
        <div className={styles.panelSubtitle}>{t('components.loginModal.wechatLoginHintSubtitle', 'Use WeChat to scan the QR code on the left')}</div>
        <div className={styles.qrcodeHint} style={{ marginTop: 24 }}>{t('components.loginModal.wechatLoginHintDescription', 'You will be logged in automatically after scanning')}</div>
        <div className={styles.backLink} onClick={handleBackToDefault}>{t('components.loginModal.backToAccountLogin', 'Back to account login')}</div>
      </div>
    </div>
  );

  const renderDefaultView = () => (
    <div className={styles.singlePanel}>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as 'login' | 'register')}
        className={styles.tabs}
        centered
        items={[
          {
            key: 'login',
            label: t('components.loginModal.tabs.login', 'Login'),
            children: <div className={styles.formContainer}>{renderLoginForm()}{renderThirdPartyLogin()}</div>,
          },
          {
            key: 'register',
            label: t('components.loginModal.tabs.register', 'Register'),
            children: <div className={styles.formContainer}>{renderRegisterForm()}</div>,
          },
        ]}
      />
    </div>
  );

  const renderContent = () => {
    if (viewMode === 'binding') return renderBindingView();
    if (viewMode === 'wechat') return <>{renderLeftPanel()}{renderWechatLoginHint()}</>;
    return renderDefaultView();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={viewMode === 'wechat' ? 600 : 420}
      centered
      destroyOnClose
      className={styles.modal}
    >
      <div className={styles.container}>{renderContent()}</div>
    </Modal>
  );
};

// ============================================
// 命令式 API（类似 Modal.confirm）
// ============================================

type LegacyRoot = { render: (node: React.ReactNode) => void; unmount: () => void };
let root: LegacyRoot | null = null;
let container: HTMLDivElement | null = null;
let currentVisible = false;
let currentOptions: { affCode?: string; defaultTab?: 'login' | 'register' } = {};

function getContainer() {
  if (!container) {
    container = document.createElement('div');
    container.id = 'global-login-modal';
    document.body.appendChild(container);
  }
  return container;
}

function render(visible: boolean, options: { affCode?: string; defaultTab?: 'login' | 'register' } = {}) {
  currentVisible = visible;
  currentOptions = options;
  const containerEl = getContainer();

  if (!root) {
    const ReactDOMClient = require('react-dom/client');
    const ReactDOM = require('react-dom');
    const maybeCreateRoot = ReactDOMClient?.createRoot as undefined | ((el: Element) => any);
    if (maybeCreateRoot) {
      root = maybeCreateRoot(containerEl) as LegacyRoot;
    } else if (ReactDOM?.createRoot) {
      root = ReactDOM.createRoot(containerEl) as LegacyRoot;
    } else {
      // Fallback for environments without createRoot
      root = {
        render: (node) => ReactDOM?.render?.(node, containerEl),
        unmount: () => ReactDOM?.unmountComponentAtNode?.(containerEl),
      };
    }
  }

  const handleClose = () => render(false);
  const handleSuccess = () => {
    render(false);
    // 移除 URL 中的 aff 参数
    const url = new URL(window.location.href);
    if (url.searchParams.has('aff')) {
      url.searchParams.delete('aff');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
    // 延迟刷新，让用户能看到成功提示
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  root.render(
    <ConfigProvider locale={getAntdLocale()}>
      <App>
        <LoginModalContent
          open={visible}
          onClose={handleClose}
          onSuccess={handleSuccess}
          affCode={options.affCode}
          defaultTab={options.defaultTab}
        />
      </App>
    </ConfigProvider>
  );
}

/**
 * 全局登录弹窗 - 命令式 API
 *
 * @example
 * import { loginModal } from '@/components/LoginModal';
 *
 * // 显示登录弹窗
 * loginModal.show();
 *
 * // 显示注册弹窗并带邀请码
 * loginModal.show({ affCode: 'xxx', defaultTab: 'register' });
 *
 * // 隐藏登录弹窗
 * loginModal.hide();
 */
export const loginModal = {
  show(options?: { affCode?: string; defaultTab?: 'login' | 'register' }) {
    render(true, options);
  },
  hide() {
    render(false);
  },
  isVisible() {
    return currentVisible;
  },
};

// 默认导出组件（用于需要在 React 树中使用的场景）
export default LoginModalContent;
